import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/authService";
import SubjectImportModal from "./SubjectImportModal";
import { getProfessionColors } from "../../utils/professionColors";

const getSubjectTypePresentation = (subjectType) => {
  switch (subjectType) {
    case "IT":
      return {
        label: "IT Module",
        icon: "fa-laptop-code",
        profession: "INFORMATIK",
      };
    case "MEDI":
      return {
        label: "MEDI Module",
        icon: "fa-palette",
        profession: "MEDIAMATIK",
      };
    case "DETAILHANDEL_FACH":
      return {
        label: "Detailhandel Fach",
        icon: "fa-store",
        profession: "DETAILHANDEL",
      };
    case "KUNDENDIALOG_FACH":
      return {
        label: "Kundendialog Fach",
        icon: "fa-headset",
        profession: "KUNDENDIALOG",
      };
    case "KV_FACH":
      return {
        label: "KV Fach",
        icon: "fa-building",
        profession: "KV",
      };
    default:
      return {
        label: "Core Subject",
        icon: "fa-graduation-cap",
        profession: null,
      };
  }
};

const SubjectManagement = () => {
  const { user } = useAuth();

  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [subjectTypeFilter, setSubjectTypeFilter] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [deletingSubject, setDeletingSubject] = useState(null);
  const [, setShowCreateForm] = useState(false);
  // const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: "",
    subjectCode: "",
    description: "",
    subjectVersion: "1.0",
    subjectType: "", // Default to core subject
  });

  // Check user authorization
  const isAuthorized =
    user &&
    user.roles &&
    (user.roles.some((role) => role.name === "ADMIN") ||
      user.roles.some((role) => role.name === "BERUFSBILDNER"));

  // Load subjects on component mount
  useEffect(() => {
    loadSubjects();
  }, []);

  const filterSubjects = useCallback(() => {
    let filtered = [...subjects];

    if (searchTerm) {
      filtered = filtered.filter(
        (subject) =>
          subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (subject.subjectCode &&
            subject.subjectCode
              .toLowerCase()
              .includes(searchTerm.toLowerCase())),
      );
    }

    if (subjectTypeFilter) {
      filtered = filtered.filter(
        (subject) => subject.subjectType === subjectTypeFilter,
      );
    }

    setFilteredSubjects(filtered);
  }, [subjects, searchTerm, subjectTypeFilter]);

  // Filter subjects when search/filter changes
  useEffect(() => {
    filterSubjects();
  }, [subjects, searchTerm, subjectTypeFilter, filterSubjects]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      const response = await api.get("/subjects");
      // Map backend snake_case to frontend camelCase to ensure consistency
      const mappedSubjects = response.data.map((s) => ({
        ...s,
        subjectType: s.subjectType || s.subject_type || "CORE",
        subjectCode: s.subjectCode || s.subject_code,
        subjectVersion: s.subjectVersion || s.subject_version || "1.0",
        isGlobal: s.isGlobal !== undefined ? s.isGlobal : s.is_global,
        isSystemSubject:
          s.isSystemSubject !== undefined
            ? s.isSystemSubject
            : s.is_system_subject,
      }));
      setSubjects(mappedSubjects);
      setError(null);
    } catch (err) {
      setError(
        "Error loading subjects: " +
          (err.response?.data?.message || err.message),
      );
      console.error("Error loading subjects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubject = async (e) => {
    e.preventDefault();

    try {
      const subjectData = {
        name: formData.name,
        description: formData.description,
        subject_code: formData.subjectCode, // Use snake_case like DB
        subject_version: formData.subjectVersion, // Use snake_case like DB
        subject_type: formData.subjectType, // Use snake_case like DB
        track: "PFLICHT",
        year: 1,
        is_global: true, // Use snake_case like DB
        validation_status: "VALID", // Use snake_case like DB
        import_source: "MANUAL", // Use snake_case like DB
        is_system_subject: false, // Use snake_case like DB
      };

      await api.post("/subjects", subjectData);
      await loadSubjects();
      setShowCreateModal(false);
      resetForm();
      setError(null);
    } catch (err) {
      console.error("Full error response:", err.response);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        `Failed to create subject (status ${err.response?.status || "unknown"})`;
      setError("Error creating subject: " + errorMessage);
    }
  };

  const handleEditSubject = async (e) => {
    e.preventDefault();

    try {
      const subjectData = {
        name: formData.name,
        description: formData.description,
        subject_code: formData.subjectCode, // Use snake_case like DB
        subject_version: formData.subjectVersion, // Use snake_case like DB
        subject_type: formData.subjectType, // Use snake_case like DB
        track: "PFLICHT",
        year: 1,
        is_global: true, // Use snake_case like DB
        validation_status: "VALID", // Use snake_case like DB
        import_source: "MANUAL", // Use snake_case like DB
        is_system_subject: false, // Use snake_case like DB
      };

      await api.put(`/subjects/${editingSubject.id}`, subjectData);
      await loadSubjects();
      closeEditModal();
      setError(null);
    } catch (err) {
      console.error("Full error response:", err.response);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        `Failed to update subject (status ${err.response?.status || "unknown"})`;
      setError("Error updating subject: " + errorMessage);
    }
  };

  const handleDeleteSubject = async () => {
    try {
      setLoading(true);

      await api.delete(`/subjects/${deletingSubject.id}`);
      await loadSubjects();
      setShowDeleteModal(false);
      setDeletingSubject(null);
      setError(null);
    } catch (err) {
      console.error("Delete error:", err);

      // If it's a constraint error, provide specific guidance
      if (err.response?.status === 400 || err.response?.status === 409) {
        const errorMessage =
          err.response?.data?.message ||
          "Cannot delete subject with existing dependencies";

        // Show a more detailed error with helpful guidance
        setError({
          type: "dependency",
          subject: deletingSubject,
          message: errorMessage,
        });
        setShowDeleteModal(false);
        setDeletingSubject(null);
        return;
      }

      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to delete subject";
      setError("Error deleting subject: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (subject) => {
    setDeletingSubject(subject);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingSubject(null);
  };

  const startEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({
      name: subject.name,
      subjectCode: subject.subjectCode || "",
      description: subject.description || "",
      subjectVersion: subject.subjectVersion || "1.0",
      subjectType: subject.subjectType || "CORE",
    });
    setShowCreateForm(false);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingSubject(null);
    resetForm();
  };

  const startCreate = () => {
    setShowCreateModal(true);
    setEditingSubject(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: "",
      subjectCode: "",
      description: "",
      subjectVersion: "1.0",
      subjectType: "",
    });
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const handleImportSuccess = () => {
    loadSubjects();
    setShowImportModal(false);
  };

  // Authorization check - matching School Management pattern
  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-ice-white flex items-center justify-center p-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-amber-600 text-2xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-grey-dusk mb-4">
            Authentication Required
          </h2>
          <p className="text-gray-600 mb-6">
            You need to be logged in as an ADMIN or BERUFSBILDNER to access
            Subject Management.
          </p>
          <button
            className="btn-brand-primary"
            onClick={() => (window.location.href = "/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-ice-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="card-brand-elevated p-6 mb-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
              <div className="flex items-center mb-4 lg:mb-0">
                <div>
                  <h1 className="text-3xl font-bold text-grey-dusk flex items-center">
                    <i className="fas fa-book-open text-sunrise-red mr-4"></i>
                    Subject Management
                  </h1>
                  <p className="text-slate-cloud mt-1">
                    Manage curriculum subjects and educational content
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="btn-brand-primary" onClick={startCreate}>
                  <i className="fas fa-plus mr-2"></i>
                  New Subject
                </button>
              </div>
            </div>
          </div>

          <div className="card-brand-elevated">
            {/* Content */}
            <div className="p-6">
              {error && (
                <div className="bg-sunrise-50 border border-sunrise-200 text-sunrise-700 px-6 py-4 rounded-lg mb-6">
                  {typeof error === "object" && error.type === "dependency" ? (
                    <div>
                      <div className="flex items-start mb-4">
                        <i className="fas fa-exclamation-triangle text-sunrise-red mr-3 mt-0.5"></i>
                        <div className="flex-1">
                          <h4 className="font-bold mb-2">
                            Cannot Delete Subject "{error.subject?.name}"
                          </h4>
                          <p className="text-sm mb-3">
                            This subject cannot be deleted because it has
                            associated grades, enrollments, or other data.
                          </p>
                          <div className="text-sm mb-4">
                            <p className="font-semibold mb-2">
                              To delete this subject, you need to:
                            </p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                              <li>Remove all grades for this subject</li>
                              <li>
                                Remove all student enrollments for this subject
                              </li>
                              <li>Then try deleting the subject again</li>
                            </ol>
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            <button
                              onClick={() => {
                                /* Navigate to grades management */
                              }}
                              className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-3 py-1 rounded-md transition-colors"
                            >
                              <i className="fas fa-chart-line mr-1"></i>
                              Manage Grades
                            </button>
                            <button
                              onClick={() => {
                                /* Navigate to student management */
                              }}
                              className="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded-md transition-colors"
                            >
                              <i className="fas fa-users mr-1"></i>
                              Manage Students
                            </button>
                          </div>
                          {error.message && (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                                Technical Details
                              </summary>
                              <p className="mt-1 text-gray-500 font-mono">
                                {error.message}
                              </p>
                            </details>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => setError(null)}
                          className="text-xs text-sunrise-600 hover:text-sunrise-800 font-medium"
                        >
                          <i className="fas fa-times mr-1"></i>
                          Dismiss
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <i className="fas fa-exclamation-triangle text-sunrise-red mr-3"></i>
                        {error}
                      </div>
                      <button
                        onClick={() => setError(null)}
                        className="text-sunrise-red hover:text-sunrise-700"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* CSV Import Section - Prominent Feature */}
              <div className="bg-gradient-to-r from-grey-dusk-50 to-indigo-50 border-2 border-grey-dusk-200 rounded-lg p-6 mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-16 h-16 bg-grey-dusk-500 rounded-full flex items-center justify-center mr-4">
                      <i className="fas fa-upload text-grey-dusk text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-1">
                        Import Subjects from CSV
                      </h3>
                      <p className="text-gray-600">
                        Upload a CSV file to bulk import subjects with codes,
                        titles, and track information
                      </p>
                    </div>
                  </div>
                  <button
                    className="btn-brand-primary bg-grey-dusk-600 hover:bg-grey-dusk-700 text-white px-8 py-3 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                    onClick={() => setShowImportModal(true)}
                  >
                    <i className="fas fa-upload mr-2"></i>
                    Import CSV
                  </button>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="card-brand p-6 mb-6">
                <h3 className="text-lg font-semibold text-grey-dusk mb-4 flex items-center">
                  <i className="fas fa-search mr-2 text-sunrise-red"></i>
                  Search & Filter
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Search Input */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <i className="fas fa-search text-gray-400"></i>
                    </div>
                    <input
                      type="text"
                      className="input-brand pl-10"
                      placeholder="Search by name or code..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Subject Type Filter */}
                  <div>
                    <select
                      className="input-brand"
                      value={subjectTypeFilter}
                      onChange={(e) => setSubjectTypeFilter(e.target.value)}
                    >
                      <option value="">All Types</option>
                      <option value="CORE">Core Subjects</option>
                      <option value="IT">IT Modules</option>
                      <option value="MEDI">MEDI Modules</option>
                      <option value="DETAILHANDEL_FACH">
                        Detailhandel Fach
                      </option>
                      <option value="KUNDENDIALOG_FACH">
                        Kundendialog Fach
                      </option>
                      <option value="KV_FACH">KV Fach</option>
                    </select>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sunrise-red mx-auto mb-4"></div>
                  <p className="text-grey-dusk text-lg">Loading subjects...</p>
                </div>
              ) : (
                <>
                  {/* Subjects Grid/Table */}
                  <div className="card-brand overflow-hidden">
                    <div className="bg-gradient-to-r from-sunrise-red to-sunrise-600 text-white px-6 py-4">
                      <h2 className="text-lg font-bold flex items-center">
                        <i className="fas fa-list mr-2"></i>
                        Subjects ({filteredSubjects.length} of {subjects.length}
                        )
                      </h2>
                    </div>

                    {filteredSubjects.length === 0 ? (
                      <div className="p-12 text-center">
                        <div className="w-32 h-32 bg-sunrise-red/10 rounded-full flex items-center justify-center mx-auto mb-6">
                          <i className="fas fa-book-open text-sunrise-red text-4xl"></i>
                        </div>
                        <h3 className="text-2xl font-bold text-grey-dusk mb-4">
                          {subjects.length === 0
                            ? "No Subjects Available"
                            : "No Matching Subjects"}
                        </h3>
                        <p className="text-gray-600 mb-8">
                          {subjects.length === 0
                            ? "Get started by creating your first subject or importing from CSV"
                            : "Try adjusting your search or filter criteria"}
                        </p>
                        {subjects.length === 0 && (
                          <div className="flex justify-center gap-4">
                            <button
                              className="btn-brand-primary"
                              onClick={startCreate}
                            >
                              <i className="fas fa-plus mr-2"></i>
                              Create First Subject
                            </button>
                            <button
                              className="btn-brand-outline"
                              onClick={() => setShowImportModal(true)}
                            >
                              <i className="fas fa-upload mr-2"></i>
                              Import CSV
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Code
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredSubjects.map((subject) => (
                              <tr
                                key={subject.id}
                                className="hover:bg-gray-50 transition-colors"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    {(() => {
                                      const presentation =
                                        getSubjectTypePresentation(
                                          subject.subjectType,
                                        );
                                      const colors = getProfessionColors(
                                        presentation.profession,
                                      );
                                      const bgClass = `bg-${colors.light}`;
                                      const textClass = `text-${colors.text}`;
                                      const iconTextClass = `text-${colors.primary}`;

                                      return (
                                        <>
                                          <div
                                            className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${bgClass}`}
                                          >
                                            <i
                                              className={`fas text-sm ${presentation.icon} ${iconTextClass}`}
                                            ></i>
                                          </div>
                                          <div className="flex flex-col">
                                            <span className="text-sm font-bold text-gray-900">
                                              {subject.subjectCode || "-"}
                                            </span>
                                            <span
                                              className={`text-xs px-2 py-1 rounded-full font-medium ${bgClass} ${textClass}`}
                                            >
                                              {presentation.label}
                                            </span>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {subject.name}
                                    </div>
                                    {subject.description && (
                                      <div className="text-sm text-gray-500 truncate max-w-xs">
                                        {subject.description}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => startEdit(subject)}
                                      className="text-sunrise-red hover:text-sunrise-600 p-2 rounded-lg hover:bg-sunrise-red/10 transition-all"
                                      title="Edit subject"
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      onClick={() => openDeleteModal(subject)}
                                      className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-all"
                                      title="Delete subject"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      <SubjectImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
      />

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-pale-dawn bg-white shadow-brand-lg">
            <div className="flex items-start justify-between border-b border-pale-dawn px-5 py-4 sm:px-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sunrise-50 text-sunrise-red">
                  <i className="fas fa-plus text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-grey-dusk">
                    Create New Subject
                  </h3>
                  <p className="mt-1 text-sm text-slate-cloud">
                    Add a new subject to the global subject list.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeCreateModal}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-grey-dusk"
                aria-label="Close modal"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            <form onSubmit={handleCreateSubject}>
              <div className="px-5 py-5 sm:px-6 sm:py-6">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="label-brand">Subject Name *</label>
                    <input
                      type="text"
                      required
                      maxLength="100"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          name: e.target.value,
                        })
                      }
                      className="input-brand"
                      placeholder="Enter subject name"
                    />
                    <p className="mt-1 text-xs text-slate-cloud">
                      {formData.name.length}/100 characters
                    </p>
                  </div>

                  <div>
                    <div>
                      <label className="label-brand">Subject Type *</label>
                      <select
                        value={formData.subjectType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subjectType: e.target.value,
                          })
                        }
                        className="input-brand"
                        required
                      >
                        <option value="" disabled>
                          Select subject type...
                        </option>

                        <option value="CORE">Core Subject</option>
                        <option value="IT">IT Module</option>
                        <option value="MEDI">MEDI Module</option>
                        <option value="DETAILHANDEL_FACH">
                          Detailhandel Fach
                        </option>
                        <option value="KUNDENDIALOG_FACH">
                          Kundendialog Fach
                        </option>
                        <option value="KV_FACH">KV Fach</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="label-brand">
                      Subject Code{" "}
                      {formData.subjectType !== "CORE" &&
                      formData.subjectType !== "KV_FACH" &&
                      formData.subjectType !== "DETAILHANDEL_FACH" &&
                      formData.subjectType !== "KUNDENDIALOG_FACH"
                        ? "*"
                        : "(Optional)"}
                    </label>
                    <input
                      type="text"
                      required={
                        formData.subjectType !== "CORE" &&
                        formData.subjectType !== "KV_FACH" &&
                        formData.subjectType !== "DETAILHANDEL_FACH" &&
                        formData.subjectType !== "KUNDENDIALOG_FACH"
                      }
                      maxLength="10"
                      value={formData.subjectCode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subjectCode: e.target.value,
                        })
                      }
                      className="input-brand"
                      placeholder={
                        formData.subjectType !== "CORE" &&
                        formData.subjectType !== "KV_FACH" &&
                        formData.subjectType !== "DETAILHANDEL_FACH" &&
                        formData.subjectType !== "KUNDENDIALOG_FACH"
                          ? "Enter module code (e.g. M106)"
                          : "Leave empty if no code is required"
                      }
                      disabled={formData.subjectType === "CORE"}
                    />
                    <p className="mt-1 text-xs text-slate-cloud">
                      {formData.subjectCode.length}/10 characters
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="label-brand">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className="input-brand min-h-[104px]"
                      rows="4"
                      placeholder="Enter subject description (optional)"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-pale-dawn px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="inline-flex items-center justify-center rounded-xl border border-pale-dawn bg-white px-5 py-2.5 text-sm font-semibold text-grey-dusk transition-colors hover:bg-pale-dawn/40"
                >
                  <i className="fas fa-times mr-2 text-xs"></i>
                  Cancel
                </button>

                <button type="submit" className="btn-brand-primary">
                  <i className="fas fa-plus mr-2 text-xs"></i>
                  Create Subject
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl rounded-2xl border border-pale-dawn bg-white shadow-brand-lg">
            <div className="flex items-start justify-between border-b border-pale-dawn px-5 py-4 sm:px-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sunrise-50 text-sunrise-red">
                  <i className="fas fa-pen text-sm"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-grey-dusk">
                    Edit Subject
                  </h3>
                  <p className="mt-1 text-sm text-slate-cloud">
                    Update the selected subject details.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={closeEditModal}
                className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-grey-dusk"
                aria-label="Close modal"
              >
                <i className="fas fa-times text-base"></i>
              </button>
            </div>

            <form onSubmit={handleEditSubject}>
              <div className="px-5 py-5 sm:px-6 sm:py-6">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="label-brand">Subject Name *</label>
                    <input
                      type="text"
                      required
                      maxLength="100"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          name: e.target.value,
                        })
                      }
                      className="input-brand"
                      placeholder="Enter subject name"
                    />
                    <p className="mt-1 text-xs text-slate-cloud">
                      {formData.name.length}/100 characters
                    </p>
                  </div>

                  <div>
                    <label className="label-brand">Subject Type *</label>
                    <select
                      value={formData.subjectType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subjectType: e.target.value,
                        })
                      }
                      className="input-brand"
                      required
                    >
                      <option value="CORE">
                        Core Subject (Mathe, Deutsch, etc.)
                      </option>
                      <option value="IT">IT Module (Modulbaukasten IT)</option>
                      <option value="MEDI">
                        MEDI Module (Modulbaukasten MEDI)
                      </option>
                      <option value="DETAILHANDEL_FACH">
                        Detailhandel Fach
                      </option>
                      <option value="KUNDENDIALOG_FACH">
                        Kundendialog Fach
                      </option>
                      <option value="KV_FACH">KV Fach</option>
                    </select>
                  </div>

                  <div>
                    <label className="label-brand">
                      Subject Code{" "}
                      {formData.subjectType !== "CORE" ? "*" : "(Optional)"}
                    </label>
                    <input
                      type="text"
                      required={formData.subjectType !== "CORE"}
                      maxLength="10"
                      value={formData.subjectCode}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subjectCode: e.target.value,
                        })
                      }
                      className="input-brand"
                      placeholder={
                        formData.subjectType !== "CORE"
                          ? "Enter module code (e.g. 187)"
                          : "Leave empty for core subjects"
                      }
                      disabled={formData.subjectType === "CORE"}
                    />
                    <p className="mt-1 text-xs text-slate-cloud">
                      {formData.subjectCode.length}/10 characters
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="label-brand">Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      className="input-brand min-h-[104px]"
                      rows="4"
                      placeholder="Enter subject description (optional)"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-pale-dawn px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="inline-flex items-center justify-center rounded-xl border border-pale-dawn bg-white px-5 py-2.5 text-sm font-semibold text-grey-dusk transition-colors hover:bg-pale-dawn/40"
                >
                  <i className="fas fa-times mr-2 text-xs"></i>
                  Cancel
                </button>

                <button type="submit" className="btn-brand-primary">
                  <i className="fas fa-save mr-2 text-xs"></i>
                  Update Subject
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingSubject && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-pale-dawn bg-white shadow-brand-lg">
            <div className="px-5 py-5 sm:px-6">
              <div className="mb-5 flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600">
                  <i className="fas fa-trash text-sm"></i>
                </div>

                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-grey-dusk">
                    Delete Subject
                  </h3>
                  <p className="mt-1 text-sm text-slate-cloud">
                    Are you sure you want to delete{" "}
                    <span className="font-semibold text-grey-dusk">
                      "{deletingSubject.name}"
                    </span>
                    ?
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-start gap-3">
                  <i className="fas fa-exclamation-triangle mt-0.5 text-sm text-amber-600"></i>
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold">Important</p>
                    <p className="mt-1 leading-6">
                      This subject can only be deleted if there are no linked
                      grades or enrolments. If the deletion fails, remove the
                      dependencies first.
                    </p>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-xs text-slate-cloud">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-pale-dawn px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="inline-flex items-center justify-center rounded-xl border border-pale-dawn bg-white px-5 py-2.5 text-sm font-semibold text-grey-dusk transition-colors hover:bg-pale-dawn/40"
              >
                <i className="fas fa-times mr-2 text-xs"></i>
                Cancel
              </button>

              <button
                type="button"
                onClick={handleDeleteSubject}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2 text-xs"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash mr-2 text-xs"></i>
                    Delete Subject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default SubjectManagement;
